import { useState, useContext } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as ChevronDown } from "bootstrap-icons/icons/chevron-down.svg";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";
import { ReactComponent as Download } from "bootstrap-icons/icons/download.svg";
import { ReactComponent as Search } from "bootstrap-icons/icons/search.svg";
import { ReactComponent as ArrowClockwise } from "bootstrap-icons/icons/arrow-clockwise.svg";
import { ReactComponent as Envelope } from "bootstrap-icons/icons/envelope.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import { handleError } from "../../components/utils/AuthenticationCheck";
import ApiServices from "../../api/ApiServices";
import { useAdminActivityLog } from "../../hooks/useAppData";
import { useQueryClient } from "@tanstack/react-query";
import AxiosApiCall from "../../api/AxiosApiCall";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import CustomStyles from "../../components/utils/CustomStyles";
import formatDate from "../../components/utils/DateFormatter";
import { AuthContext } from "../../AuthProvider/AuthProvider";
import { downloadCSVFromApi } from "../../components/utils/DownlaodCSVFromApi";

const AdminActLog = () => {
    const [loadingCSV, setLoadingCSV] = useState(false);

    // Search states (won't trigger API until Apply is clicked)
    const [tempFilterText, setTempFilterText] = useState("");
    const [tempEmailFilter, setTempEmailFilter] = useState("");

    // Applied filter states — changes here trigger React Query re-fetch
    const [appliedFilterText, setAppliedFilterText] = useState("");
    const [appliedEmailFilter, setAppliedEmailFilter] = useState("");

    const [perPage, setPerPage] = useState(1000);
    const [page, setPage] = useState(1);

    const { permissions } = useContext(AuthContext);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Date filter state
    const [filters, setFilters] = useState({
        start_date: "",
        end_date: "",
    });

    const queryFilters = {
        page,
        per_page: perPage,
        search: appliedFilterText,
        email: appliedEmailFilter,
        start_date: filters.start_date,
        end_date: filters.end_date,
    };

    const { data: activitiesResponse, isLoading: loading } = useAdminActivityLog(queryFilters);
    const activities = activitiesResponse?.data?.activities || [];
    const totalRows = activitiesResponse?.data?.total || 0;

    // ===================== PAGINATION =====================
    const handlePageChange = (newPage) => setPage(newPage);

    const handlePerRowsChange = (newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(newPage);
    };

    // ===================== SEARCH HANDLERS =====================
    const handleFilter = (e) => {
        setTempFilterText(e.target.value);
    };

    const handleEmailFilter = (e) => {
        setTempEmailFilter(e.target.value);
    };

    // ===================== APPLY ALL FILTERS =====================
    const handleApplyAllFilters = () => {
        // Validate date range if dates are selected
        if (filters.start_date && filters.end_date) {
            const start = new Date(filters.start_date);
            const end = new Date(filters.end_date);

            if (start > end) {
                toast.error("Start date cannot be after end date");
                return;
            }
        }

        // Apply all filters and reset to page 1 — React Query auto re-fetches
        setAppliedFilterText(tempFilterText);
        setAppliedEmailFilter(tempEmailFilter);
        setPage(1);

        toast.success("Filters applied");
    };

    // ===================== CLEAR ALL FILTERS =====================
    const handleClearAllFilters = () => {
        // Clear temporary filters
        setTempFilterText("");
        setTempEmailFilter("");

        // Clear applied filters
        setAppliedFilterText("");
        setAppliedEmailFilter("");

        // Clear date filters
        setFilters({
            start_date: "",
            end_date: "",
        });

        setPage(1);
        toast.success("All filters cleared");
    };

    // ===================== TABLE COLUMNS =====================
    const columns = [
        {
            name: "Date",
            selector: (row) =>
                row.created_at ? formatDate(row.created_at) : "NOT AVAILABLE",
            sortable: true,
            width: "190px",
        },
        {
            name: "Name",
            selector: (row) => row.name || "NOT AVAILABLE",
            sortable: true,
            width: "150px",
        },
        {
            name: "Email",
            selector: (row) => row.email || "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Role",
            selector: (row) => row.role || "NOT AVAILABLE",
            sortable: true,
            width: "150px",
        },
        {
            name: "Action",
            selector: (row) => row.action || "NOT AVAILABLE",
            sortable: true,
            width: "300px",
        },
        {
            name: "Status",
            width: "120px",
            cell: (row) => (
                <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === "success"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                        }`}
                >
                    {row.status?.toUpperCase() || "NOT AVAILABLE"}
                </span>
            ),
        },
    ];

    // ===================== CSV DOWNLOAD =====================
    const handleDownloadCSV = async () => {
        if (!filters.start_date || !filters.end_date) {
            toast.error("Please select both start and end dates for download");
            return;
        }

        setLoadingCSV(true);

        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;

            // Prepare download parameters
            const params = {
                start_date: filters.start_date,
                end_date: filters.end_date,
                download: 1,
                sort_by: "created_at",
                sort_order: "desc",
            };

            // Add applied search filters to CSV download
            if (appliedFilterText) {
                params.search = appliedFilterText;
            }
            if (appliedEmailFilter) {
                params.email = appliedEmailFilter;
            }

            await downloadCSVFromApi(
                ApiServices.getAdminActivityLog,
                params,
                token,
                {
                    filename: `admin_activity_${filters.start_date}_to_${filters.end_date}.csv`,
                }
            );
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setLoadingCSV(false);
        }
    };

    // ===================== RELOAD =====================
    const handleReload = () => {
        queryClient.invalidateQueries({ queryKey: ["adminActivityLog"] });
        toast.success("Activity log refreshed");
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    // Handle Enter key in search fields
    const handleSearchEnter = (e) => {
        if (e.key === 'Enter') {
            handleApplyAllFilters();
        }
    };

    // Check if any filter has unsaved changes
    const hasUnsavedChanges = () => {
        return tempFilterText !== appliedFilterText ||
            tempEmailFilter !== appliedEmailFilter;
    };

    return (
        <div>
            <MainLayouts>
                <div className="sm:rounded-lg py-6 w-full">
                    <section
                        className="w-full pr-[3%] pt-3 my-3"
                        style={{ backgroundColor: "#F7F7F7" }}
                    >
                        <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                            <ol className="list-none p-0 inline-flex mb-2">
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                        Admin Activity
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-orange1 hover:text-blue-600">List</p>
                                </li>
                            </ol>
                        </nav>
                    </section>

                    {/* Filter Controls Section - Responsive */}
                    <div className="pb-4 bg-white dark:bg-gray-900">
                        {/* DESKTOP FILTERS (md and up) - All in one row */}
                        <div className="hidden md:flex flex-wrap items-center gap-3">
                            {/* Global Search Input Field */}
                            <div className="relative flex-grow min-w-[200px]">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="search"
                                        className="block w-full pl-10 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        placeholder="Search all fields..."
                                        value={tempFilterText}
                                        onChange={handleFilter}
                                        onKeyDown={handleSearchEnter}
                                    />
                                    {tempFilterText && (
                                        <button
                                            onClick={() => setTempFilterText("")}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            title="Clear search"
                                        >
                                            <XCircle className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>

                          

                            {/* Start Date Input */}
                            <div className="min-w-[150px]">
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={filters.start_date}
                                        onChange={(e) =>
                                            handleFilterChange("start_date", e.target.value)
                                        }
                                        className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                                        max={filters.end_date || undefined}
                                        placeholder="Start Date"
                                    />
                                </div>
                            </div>

                            {/* End Date Input */}
                            <div className="min-w-[150px]">
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={filters.end_date}
                                        onChange={(e) =>
                                            handleFilterChange("end_date", e.target.value)
                                        }
                                        className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                                        min={filters.start_date || undefined}
                                        placeholder="End Date"
                                    />
                                </div>
                            </div>

                            {/* Apply All Filters Button */}
                            <div className="min-w-[140px]">
                                <button
                                    onClick={handleApplyAllFilters}
                                    disabled={!tempFilterText && !tempEmailFilter && !filters.start_date && !filters.end_date}
                                    className={`w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${tempFilterText || tempEmailFilter || filters.start_date || filters.end_date
                                        ? "bg-darkGreen3 text-lGrey hover:bg-green-700"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                >
                                    {hasUnsavedChanges() ? (
                                        <>
                                            <ArrowClockwise className="w-4 h-4" />
                                            Apply Filters
                                        </>
                                    ) : (
                                        "Apply Filters"
                                    )}
                                </button>
                            </div>

                            {/* Clear All Filters Button */}
                            <div className="min-w-[120px]">
                                <button
                                    onClick={handleClearAllFilters}
                                    disabled={!appliedFilterText && !appliedEmailFilter && !filters.start_date && !filters.end_date}
                                    className={`w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${appliedFilterText || appliedEmailFilter || filters.start_date || filters.end_date
                                        ? "bg-black1 text-lGrey hover:bg-gray-700"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                >
                                    <XCircle className="w-4 h-4" />
                                    Clear All
                                </button>
                            </div>

                            {/* Download Button */}
                            <div className="min-w-[160px]">
                                <button
                                    onClick={handleDownloadCSV}
                                    className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${filters.start_date && filters.end_date
                                        ? "bg-orange1 text-lGrey hover:bg-orange-600 cursor-pointer"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                    disabled={!filters.start_date || !filters.end_date || loadingCSV}
                                    title={
                                        !filters.start_date || !filters.end_date
                                            ? "Select start and end dates to enable download"
                                            : ""
                                    }
                                >
                                    {loadingCSV ? (
                                        <>
                                            <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                            Downloading...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4" />
                                            Download CSV
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* MOBILE FILTERS (sm and below) - Stacked */}
                        <div className="md:hidden space-y-3">
                            <div className="grid grid-cols-1 gap-3">
                                {/* Global Search */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="search"
                                        className="block w-full pl-10 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                                        placeholder="Search all fields..."
                                        value={tempFilterText}
                                        onChange={handleFilter}
                                        onKeyDown={handleSearchEnter}
                                    />
                                    {tempFilterText && (
                                        <button
                                            onClick={() => setTempFilterText("")}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <XCircle className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>

                               
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                {/* Start Date */}
                                <div>
                                    <input
                                        type="date"
                                        value={filters.start_date}
                                        onChange={(e) =>
                                            handleFilterChange("start_date", e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                        max={filters.end_date || undefined}
                                        placeholder="Start Date"
                                    />
                                </div>

                                {/* End Date */}
                                <div>
                                    <input
                                        type="date"
                                        value={filters.end_date}
                                        onChange={(e) =>
                                            handleFilterChange("end_date", e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                        min={filters.start_date || undefined}
                                        placeholder="End Date"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                {/* Apply Filters Button */}
                                <button
                                    onClick={handleApplyAllFilters}
                                    disabled={!tempFilterText && !tempEmailFilter && !filters.start_date && !filters.end_date}
                                    className={`inline-flex justify-center items-center px-4 py-2.5 text-sm font-medium rounded-lg ${tempFilterText || tempEmailFilter || filters.start_date || filters.end_date
                                        ? "bg-darkGreen3 text-lGrey"
                                        : "bg-gray-300 text-gray-500"
                                        }`}
                                >
                                    Apply
                                </button>

                                {/* Clear All Button */}
                                <button
                                    onClick={handleClearAllFilters}
                                    disabled={!appliedFilterText && !appliedEmailFilter && !filters.start_date && !filters.end_date}
                                    className={`inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium rounded-lg ${appliedFilterText || appliedEmailFilter || filters.start_date || filters.end_date
                                        ? "bg-black1 text-lGrey"
                                        : "bg-gray-300 text-gray-500"
                                        }`}
                                >
                                    <XCircle className="w-4 h-4" />
                                    Clear
                                </button>

                                {/* Download Button */}
                                <button
                                    onClick={handleDownloadCSV}
                                    disabled={!filters.start_date || !filters.end_date || loadingCSV}
                                    className={`inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-semibold rounded-lg ${filters.start_date && filters.end_date
                                        ? "bg-orange1 text-white"
                                        : "bg-gray-300 text-gray-500"
                                        }`}
                                >
                                    <Download className="w-4 h-4" />
                                    {loadingCSV ? "..." : "CSV"}
                                </button>
                            </div>
                        </div>

                        {/* Active Filters Indicator */}
                        {(appliedFilterText || appliedEmailFilter || filters.start_date || filters.end_date) && (
                            <div className="mt-3 text-sm text-gray-600">
                                <span className="font-medium">Active filters:</span>
                                {appliedFilterText && (
                                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                        Search: "{appliedFilterText}"
                                    </span>
                                )}
                                {appliedEmailFilter && (
                                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                        Email: "{appliedEmailFilter}"
                                    </span>
                                )}
                                {filters.start_date && filters.end_date && (
                                    <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                                        Date: {filters.start_date} to {filters.end_date}
                                    </span>
                                )}
                            </div>
                        )}


                        {/* Results Count */}
                        {/* <div className="mt-2 text-sm text-gray-500">
                            Showing {activities.length} of {totalRows} records
                            {(appliedFilterText || appliedEmailFilter || filters.start_date || filters.end_date) && " (filtered)"}
                        </div> */}
                    </div>

                    {/* Data Table */}
                    <DataTable
                        columns={columns}
                        data={activities}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationDefaultPage={page}
                        paginationPerPage={perPage}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        paginationRowsPerPageOptions={[10, 25, 50, 100, 500, 1000]}
                        striped
                        highlightOnHover
                        customStyles={CustomStyles}
                        progressPending={loading}
                        progressComponent={<Spinner loading={loading} />}
                        noDataComponent={
                            <div className="text-center py-8">
                                {loading ? "" : (appliedFilterText || appliedEmailFilter || filters.start_date || filters.end_date
                                    ? "No activities match your filters"
                                    : "No admin activity log found"
                                )}
                            </div>
                        }
                        defaultSortFieldId="created_at"
                        defaultSortAsc={false}
                    />
                </div>
            </MainLayouts>
        </div>
    );
};

export default AdminActLog;